<template>
  <div v-if="evidenceDataReady">
    <section class="scope-review-approvals">
      <k-panel
        :hasContentToggle="false"
        title="Approve Project"
      >
        <template #body>
          <p>
            Once all evidence is uploaded and feedback has been resolved, click 'Complete Project' to approve the project and mark it as complete.
          </p>
          <p>
            <strong>NOTE:</strong> Highlighted rows indicate evidence that still requires review.
          </p>
          <div v-if="!projectCompleted" class="button-container">
            <button class="btn btn-success" @click="handleApprovalProcess" :disabled="approvalDisabled">
              Complete Project
            </button>
          </div>
          <div v-else class="approved-container">
            <k-status-pill type="success" statusValue="approved">
              <template #label>
                <i class="fas fa-check" aria-hidden="true"></i> Project approved
              </template>
            </k-status-pill>
          </div>
        </template>
      </k-panel>
    </section>
    <section class="evidence-table">
      <k-panel
        :hasContentToggle="false"
        title="Evidence"
      >
        <template #body>
          <k-table
            :headers="tableHeaders"
            :rows="tableRows"
            :hideable="false"
            :downloadable="false"
            :panel="false"
            :max="10"
            @clicked="handleTableAction"
            :class-func="getRowClass"
            class="evidence-table"
            />
        </template>
      </k-panel>
    </section>
  </div>
</template>

<style scoped>
.button-container,
.approved-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.evidence-table :deep(.competency-pill) {
  display: inline-block;
  font-size: 0.9em;
  letter-spacing: 0.4px;
  border-radius: 15px;
  line-height: inherit;
  padding: 3px 10px;
  margin: 2px 5px 2px 0;
}

.evidence-table :deep(.competency-pill.approved) {
  color: #000;
  background-color: var(--kate-success);
}

.evidence-table :deep(.competency-pill.pending) {
  background-color: var(--kate-button-outlined-hover);
  color: var(--kate-type-dark);
}

[data-theme="light"] .evidence-table :deep(.competency-pill.pending) {
  border: 1px solid var(--kate-type-dark);
}

.evidence-table :deep(.k-table-row.review-highlight) {
  border-left: 5px solid var(--kate-warning);
}

.evidence-table :deep(.k-table-row.review-highlight .k-table-cell) {
  color: #fff !important;
  font-weight: bold !important;
}

[data-theme="light"] .evidence-table :deep(.k-table-row.review-highlight .k-table-cell) {
  color: #000 !important;
  font-weight: bold !important;
}
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import KStatusPill from '@base-components/k-status-pill.vue';

export default {
  name: 'ProjectReviewEvidence',
  props: {
    projectData: {
      type: Object,
      default: null,
    },
  },
  components: {
    KPanel,
    KTable,
    KStatusPill,
  },
  data() {
    return {
      evidenceData: [],
      evidenceDataReady: false,
      isUpdating: false,
    };
  },

  computed: {
    projectCompleted() {
      return this.projectData?.project_completed;
    },
    evidenceComplete() {
      return this.evidenceData.every(evidence => evidence.approved);
    },
    approvalDisabled() {
      return !this.evidenceComplete || this.isUpdating || this.projectCompleted || !this.projectData?.scoping_completed;
    },

    tableHeaders() {
      return {
        name: {
          name: 'Name',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        description: {
          name: 'Description',
          type: 'string',
          filterable: true,
        },
        evidenceUploaded: {
          name: 'Evidence',
          type: 'button',
          options: row => this.getEvidenceDownloadBtnConfig(row),
        },
        competencies: {
          name: 'Competencies',
          type: 'function',
          displayAs: row => {
            if (!row.competencies || row.competencies.length === 0) return '-';
            return row.competencies.map(comp => {
              const pillClass = comp.status ? 'approved' : 'pending';
              return `<span class="competency-pill ${pillClass}">${comp.name}</span>`;
            }).join(' ');
          },
        },
        reviewEvidence: {
          name: 'Review',
          type: 'button',
          options: {
            preset: 'open',
            title: evidence => `Review ${evidence.name}`,
            ariaLabel: evidence => (evidence.pending_review ? 'New evidence needs review' : 'Review evidence'),
            variant: evidence => (evidence.pending_review ? 'warning' : 'primary'),
          },
        },
        approved: {
          name: 'Approved',
          type: 'boolean',
        },
      };
    },

    tableRows() {
      return this.evidenceData.map(evidence => ({
        id: evidence.project_evidence_id,
        name: evidence.name,
        description: evidence.description,
        competencies: (evidence.competency_mappings || []).map(c => ({ name: c.competency_name, status: c.status })),
        evidenceUploaded: { evidenceLink: evidence.evidence_link, signedUrl: evidence.signed_url, fileName: evidence.filename },
        approved: evidence.approved,
        pending_review: evidence.pending_review,
      }));
    },

  },

  mounted() {
    if (this.projectData) {
      this.getEvidence();
    }
  },

  methods: {
    getEvidence() {
      this.$Loading.start();
      this.evidenceDataReady = false;
      this.$logger.info('Fetching all evidence', this.projectData.id);
      this.$http.get(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence`)
        .then(response => {
          this.$logger.info('Successfully fetched evidence', this.projectData.id);
          this.evidenceData = response.data || [];
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching evidence', this.projectData.id, err);
          this.$ktoast.warning('Error fetching evidence');
          this.evidenceData = [];
        }).then(() => {
          this.evidenceDataReady = true;
          this.$Loading.finish();
        });
    },

    handleApprovalProcess() {
      const msg = 'Please confirm you have reviewed and approved all the evidence, and want to mark the project as completed.';
      this.$ktoast.confirmToast(msg, 'warning', () => this.approveProject(), undefined);
    },

    async approveProject() {
      this.isUpdating = true;
      this.$logger.info('Approving project completion', { projectId: this.projectData.id });
      this.$Loading.start();
      const payload = {
        programme_id: this.projectData.programme_id,
        name: this.projectData.name,
        description: this.projectData.description,
        project_type: 'portfolio',
        status: 'completed',
        scoping_completed: true,
        project_completed: true,
      };

      this.$http.put(`/api/curriculum/admin/programmes/projects/${this.projectData.id}/learner/${this.projectData.user_id}`, payload)
        .then(() => {
          this.$logger.info('Project completed successfully', { projectId: this.projectData.id });
          this.$ktoast.success('Project completed successfully');
          // Reload project data to reflect completion status changes
          this.$emit('project-updated');
        })
        .catch(err => {
          this.$logger.autowarn('Error completing project', { projectId: this.projectData.id }, err);
          this.$ktoast.warning('Error completing project', err);
        })
        .then(() => {
          this.isUpdating = false;
          this.$Loading.finish();
        });
    },

    getRowClass(row) {
      return !row.approved && row.pending_review ? 'review-highlight' : '';
    },

    handleTableAction(action, index, row, key) {
      if (key === 'reviewEvidence') {
        this.viewEvidence(row);
      }
      // Download actions handled by k-table-button
    },

    viewEvidence(row) {
      const evidenceId = row && row.id;
      const projectId = this.projectData && this.projectData.id;
      if (!evidenceId || !projectId) return;
      this.$router.push({
        name: 'project_review_evidence_detail',
        params: {
          projectId,
          evidenceId,
        },
      });
    },

    getEvidenceDownloadBtnConfig(evidence) {
      const { evidenceLink, signedUrl, fileName } = evidence.evidenceUploaded || {};
      return {
        preset: signedUrl ? 'download-file' : 'download-link',
        downloadUrl: signedUrl || evidenceLink,
        title: signedUrl ? `Download ${fileName || 'file'}` : `Open link: ${evidenceLink}`,
        text: 'Download',
        ariaLabel: signedUrl ? 'Download evidence file' : 'Open evidence link',
      };
    },
  },
};
</script>
